import type {
  ISupplierProduct,
  ISupplierProductWithItemsPropertyPopulated,
} from './../types/supplierProduct/models'
import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import { makeGetRouteForLastSelectedTab } from './factories'
import type { TEntityId, IEntitySelectItem } from '~/types/models'
import type {
  ISupplier,
  ISupplierOverview,
  ISupplierLastSelectedState,
} from '~/types/supplier/models'
import type { ITsdSupplier } from '~/types/tsd/models'
import type { ISupplierContact } from '~/types/contact/models'
import {
  SupplierProduct,
  collectOptionalItemsWithContext,
  getSupplierProductLabel,
  stripRootLevelProductGroup,
} from '~/cynqueModules/dataObjects/SupplierProduct'
import { SupplierContact } from '~/cynqueModules/dataObjects/SupplierContact'

const apiEntityPath = '/api/suppliers'

export async function getSuppliers({ apiPath = apiEntityPath } = {}) {
  const callingMn = getSuppliers.name
  return httpGet<ISupplier[]>({ callingMn, apiPath })
}

export async function getSupplierOverview(id: string) {
  const callingMn = getSupplierOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  const overview = await httpGet<ISupplierOverview>({ callingMn, apiPath })
  // TEMP TESTING SHIM — strip root-level productGroup off the embedded
  // supplier-products (see stripRootLevelProductGroup).
  overview.supplierProducts = (overview.supplierProducts || []).map((sp) =>
    stripRootLevelProductGroup(sp)
  )
  return overview
}

export async function getLastSelectedStateForSupplier(id: TEntityId) {
  const callingMn = getLastSelectedStateForSupplier.name
  const apiPath = `${apiEntityPath}/${id}/lastselectedstate`
  return httpGet<ISupplier>({ callingMn, apiPath })
}

export const getRouteForLastSelectedTab =
  makeGetRouteForLastSelectedTab<ISupplierLastSelectedState>(
    apiEntityPath,
    'Suppliers-Overview'
  )

export async function getRouteAsPathForLastSelectedTab(id: string) {
  const data = await getRouteForLastSelectedTab(id)
  const path = '/' + data.replaceAll('-', '/').replace('/id', `/${id}`)
  console.debug(`getRouteAsPathForLastSelectedTab`, path)
  return path
}

export async function getSupplierProductsList(id: string) {
  const callingMn = getSupplierProductsList.name
  const apiPath = `${apiEntityPath}/${id}/supplierProducts`
  const rawSupplierProducts = await httpGet<ISupplierProduct[]>({
    callingMn,
    apiPath,
  })
  return rawSupplierProducts.map((supplierProduct) => {
    // TEMP TESTING SHIM — strip root-level productGroup before labeling.
    const stripped = stripRootLevelProductGroup(supplierProduct)
    return Object.assign(stripped, SupplierProduct(stripped))
  })
}

export async function getTsdSuppliersBySupplierId(id: TEntityId) {
  const callingMn = getTsdSuppliersBySupplierId.name
  const apiPath = `${apiEntityPath}/${id}/tsdSuppliers`
  return httpGet<ITsdSupplier[]>({ callingMn, apiPath })
}

export async function getContactsForSupplierIdPreProcessed(id: TEntityId) {
  const callingMn = getContactsForSupplierIdPreProcessed.name
  const apiPath = `${apiEntityPath}/${id}/contacts`
  const rawContacts = await httpGet<ISupplierContact[]>({
    callingMn,
    apiPath,
  })
  return rawContacts.map((rawContact) =>
    Object.assign(rawContact, SupplierContact(rawContact))
  )
}

export async function getSupplierOpportunityRegistrationEmailAddresses(
  id: string
) {
  const callingMn = getSupplierOpportunityRegistrationEmailAddresses.name
  const apiPath = `${apiEntityPath}/${id}/opportunityregistrationemailaddresses`
  return httpGet<string[]>({ callingMn, apiPath })
}

export async function getProductGroupsForSupplierList(id: string) {
  const callingMn = getProductGroupsForSupplierList.name
  const apiPath = `${apiEntityPath}/${id}/productGroups`
  // Each group carries its parent chain: { id, label, parent: subcategory{ id,
  // label, parent: category{ id, label } } }.
  return httpGet<IEntitySelectItem[]>({ callingMn, apiPath })
}

// Derive a supplier's product hierarchy (only the categories/subcategories/
// groups the supplier actually carries) from the single parent-bearing groups
// list, so a supplier-first form can cascade Category → Subcategory → Group
// without drilling into dead ends.
export async function getSupplierProductHierarchySelectItems(
  supplierId: string
) {
  const groups = await getProductGroupsForSupplierList(supplierId)
  const categoriesById = new Map<TEntityId, IEntitySelectItem>()
  const subcatsByCategoryId = new Map<
    TEntityId,
    Map<TEntityId, IEntitySelectItem>
  >()
  const groupsBySubcategoryId = new Map<TEntityId, IEntitySelectItem[]>()
  for (const group of groups) {
    const subcategory = group.parent
    const category = subcategory?.parent
    if (!subcategory || !category) {
      continue
    }
    categoriesById.set(category.id, category)
    if (!subcatsByCategoryId.has(category.id)) {
      subcatsByCategoryId.set(category.id, new Map())
    }
    subcatsByCategoryId.get(category.id)!.set(subcategory.id, subcategory)
    if (!groupsBySubcategoryId.has(subcategory.id)) {
      groupsBySubcategoryId.set(subcategory.id, [])
    }
    groupsBySubcategoryId.get(subcategory.id)!.push(group)
  }
  const byLabel = (a: IEntitySelectItem, b: IEntitySelectItem) =>
    a.label.localeCompare(b.label)
  return {
    categories: [...categoriesById.values()].sort(byLabel),
    subcategoriesByCategoryId: new Map(
      [...subcatsByCategoryId].map(([id, m]) => [
        id,
        [...m.values()].sort(byLabel),
      ])
    ),
    groupsBySubcategoryId,
  }
}

export async function getSupplierProductWithItemsForSupplierAndProduct(
  supplierId: string,
  productId: string,
  wellFormedSupplierProductsOnly: boolean = false
) {
  const callingMn = getSupplierProductWithItemsForSupplierAndProduct.name
  const apiPath =
    `${apiEntityPath}/${supplierId}/product/${productId}/withitems` +
    (wellFormedSupplierProductsOnly
      ? '?wellformedsupplierproductsonly=true'
      : '')
  const supplierProduct =
    await httpGet<ISupplierProductWithItemsPropertyPopulated>({
      callingMn,
      apiPath,
    })
  // TEMP TESTING SHIM — strip root-level productGroup (recurses children).
  return stripRootLevelProductGroup(supplierProduct)
}

export async function getSupplierProductsWithItemsForSuppliersAndProduct(
  supplierIds: string[],
  productId: string,
  wellFormedSupplierProductsOnly: boolean = false
) {
  const fn = getSupplierProductsWithItemsForSuppliersAndProduct.name
  const supplierProducts: ISupplierProductWithItemsPropertyPopulated[] = []
  for (let index = 0; index < supplierIds.length; index++) {
    const compositeData =
      await getSupplierProductWithItemsForSupplierAndProduct(
        supplierIds[index],
        productId,
        wellFormedSupplierProductsOnly
      )
    console.debug(`${fn} data:`, compositeData)
    supplierProducts.push(compositeData)
  }
  console.log(`${fn} supplierProducts w/Items:`, supplierProducts)
  return supplierProducts
}

export async function getSupplierProductsOptionalItemsForSuppliersAndProduct(
  supplierIds: string[],
  productId: string
) {
  const fn = getSupplierProductsOptionalItemsForSuppliersAndProduct.name
  const supplierProducts: ISupplierProduct[] = []
  for (let index = 0; index < supplierIds.length; index++) {
    const compositeData =
      await getSupplierProductWithItemsForSupplierAndProduct(
        supplierIds[index],
        productId
      )
    console.debug(`${fn} data:`, compositeData)
    supplierProducts.push(compositeData)
  }
  console.log(`${fn} supplierProducts:`, supplierProducts)

  const justItems = supplierProducts.flatMap((sp) =>
    collectOptionalItemsWithContext({ supplierProduct: sp }).map((item) => {
      const supplierSegment =
        item.supplierProduct.supplier?.descr ?? sp.supplier.descr
      let spSegment =
        item.parentSupplierProductItem?.descr ||
        getSupplierProductLabel(item.supplierProduct) ||
        getSupplierProductLabel(sp)
      const supplierPrefix = supplierSegment ? `${supplierSegment} ` : undefined
      if (supplierPrefix && spSegment?.startsWith(supplierPrefix)) {
        spSegment = spSegment.slice(supplierPrefix.length)
      }
      item.label =
        `${supplierSegment}` +
        ` | ${spSegment}` +
        ` | ${item.descr || item.product.descr} `
      // The same three segments, kept apart for optionSections.ts: the
      // supplier heads a group, the supplier-product titles a section.
      return Object.assign(item, {
        _optionGroupDescr: supplierSegment,
        _optionSectionDescr: spSegment,
        _optionDescr: item.descr || item.product.descr,
      })
    })
  )
  console.log(`${fn} just optional items with labels:`, justItems)

  const justItemsPlusDividers: any[] = []
  justItems.forEach((item, index) => {
    justItemsPlusDividers.push(item)
    if (index < justItems.length - 1) {
      if (
        justItems[index + 1]?._selectUid.split('__')[1] !==
          item?._selectUid.split('__')[1] &&
        justItems[index + 1]?.label.split('|')[0] === item?.label.split('|')[0]
      ) {
        // 2 INSET dividers at a supplier-product boundary WITHIN one supplier;
        // 4 plain ones below at a supplier-to-supplier boundary. Stacking N of
        // them is a Vuetify 2 spacing hack for "bigger boundary".
        //
        // V3 shape: Vuetify 2 read a bare `divider: true` on a select item;
        // Vuetify 3 keys off `item.type` (VList.js:25 itemTypes) and renders a
        // real VDivider for it (VSelect.js:514). Under V2's shape the entry had
        // no title and stayed selectable, so each one showed as a blank,
        // caption-less checkbox in the menu. Per-item props go under `props`.
        for (let i = 0; i < 2; i++) {
          justItemsPlusDividers.push({
            type: 'divider',
            props: { inset: true },
          })
        }
      }
      if (
        justItems[index + 1]?.label.split('|')[0] !== item?.label.split('|')[0]
      ) {
        for (let i = 0; i < 4; i++) {
          justItemsPlusDividers.push({ type: 'divider' })
        }
      }
    }
  })
  console.log(
    `${fn} just optional items with labels and dividers:`,
    justItemsPlusDividers
  )
  return justItemsPlusDividers
}

export async function createSupplier(entity: ISupplier) {
  const callingMn = createSupplier.name
  const apiPath = apiEntityPath
  return httpPost<ISupplier>({ callingMn, apiPath, entity })
}

export async function updateSupplier(
  entityData: Partial<ISupplier>,
  id: TEntityId
) {
  const callingMn = updateSupplier.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialISupplier = Partial<ISupplier>
  return httpPatchMerge<TPartialISupplier>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteSupplier(entityData: ISupplier) {
  const callingMn = deleteSupplier.name
  const apiPath = apiEntityPath
  return httpDelete({
    callingMn,
    apiPath,
    entityData,
  })
}
